import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Search, BookOpen, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";

const BIBLE_API = "https://bible-api.com";

const books = [
  { name: "Genesis", chapters: 50 }, { name: "Exodus", chapters: 40 }, { name: "Leviticus", chapters: 27 },
  { name: "Numbers", chapters: 36 }, { name: "Deuteronomy", chapters: 34 }, { name: "Joshua", chapters: 24 },
  { name: "Judges", chapters: 21 }, { name: "Ruth", chapters: 4 }, { name: "1 Samuel", chapters: 31 },
  { name: "2 Samuel", chapters: 24 }, { name: "1 Kings", chapters: 22 }, { name: "2 Kings", chapters: 25 },
  { name: "1 Chronicles", chapters: 29 }, { name: "2 Chronicles", chapters: 36 }, { name: "Ezra", chapters: 10 },
  { name: "Nehemiah", chapters: 13 }, { name: "Esther", chapters: 10 }, { name: "Job", chapters: 42 },
  { name: "Psalms", chapters: 150 }, { name: "Proverbs", chapters: 31 }, { name: "Ecclesiastes", chapters: 12 },
  { name: "Song of Solomon", chapters: 8 }, { name: "Isaiah", chapters: 66 }, { name: "Jeremiah", chapters: 52 },
  { name: "Lamentations", chapters: 5 }, { name: "Ezekiel", chapters: 48 }, { name: "Daniel", chapters: 12 },
  { name: "Hosea", chapters: 14 }, { name: "Joel", chapters: 3 }, { name: "Amos", chapters: 9 },
  { name: "Obadiah", chapters: 1 }, { name: "Jonah", chapters: 4 }, { name: "Micah", chapters: 7 },
  { name: "Nahum", chapters: 3 }, { name: "Habakkuk", chapters: 3 }, { name: "Zephaniah", chapters: 3 },
  { name: "Haggai", chapters: 2 }, { name: "Zechariah", chapters: 14 }, { name: "Malachi", chapters: 4 },
  { name: "Matthew", chapters: 28 }, { name: "Mark", chapters: 16 }, { name: "Luke", chapters: 24 },
  { name: "John", chapters: 21 }, { name: "Acts", chapters: 28 }, { name: "Romans", chapters: 16 },
  { name: "1 Corinthians", chapters: 16 }, { name: "2 Corinthians", chapters: 13 }, { name: "Galatians", chapters: 6 },
  { name: "Ephesians", chapters: 6 }, { name: "Philippians", chapters: 4 }, { name: "Colossians", chapters: 4 },
  { name: "1 Thessalonians", chapters: 5 }, { name: "2 Thessalonians", chapters: 3 }, { name: "1 Timothy", chapters: 6 },
  { name: "2 Timothy", chapters: 4 }, { name: "Titus", chapters: 3 }, { name: "Philemon", chapters: 1 },
  { name: "Hebrews", chapters: 13 }, { name: "James", chapters: 5 }, { name: "1 Peter", chapters: 5 },
  { name: "2 Peter", chapters: 3 }, { name: "1 John", chapters: 5 }, { name: "2 John", chapters: 1 },
  { name: "3 John", chapters: 1 }, { name: "Jude", chapters: 1 }, { name: "Revelation", chapters: 22 },
];

const otBooks = books.slice(0, 39);
const ntBooks = books.slice(39);

export default function BiblePage() {
  const [view, setView] = useState("books"); // books, chapters, reading
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchChapter = async (bookName, chapter) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BIBLE_API}/${bookName}+${chapter}?translation=kjv`);
      setVerses(res.data.verses || []);
      setSelectedChapter(chapter);
      setView("reading");
    } catch (err) {
      setVerses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchResults(null);
    try {
      const res = await axios.get(`${BIBLE_API}/${searchQuery.trim()}?translation=kjv`);
      setSearchResults(res.data);
    } catch {
      setSearchResults({ error: true });
    } finally {
      setSearchLoading(false);
    }
  };

  const goBack = () => {
    if (view === "reading") {
      setView("chapters");
      setVerses([]);
    } else if (view === "chapters") {
      setView("books");
      setSelectedBook(null);
    }
  };

  return (
    <div data-testid="bible-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Scripture
          </p>
          <h1 data-testid="bible-headline" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
            KJV Bible
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            Read and search the King James Version of the Holy Bible.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Search */}
          <form onSubmit={handleSearch} data-testid="bible-search-form" className="mb-10">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
                <Input
                  data-testid="bible-search-input"
                  placeholder="Search a verse (e.g. John 3:16, Psalm 23, Romans 8:28)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-[#E5E2DC] bg-[#FAF9F6] focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
                />
              </div>
              <button
                type="submit"
                data-testid="bible-search-btn"
                disabled={searchLoading}
                className="bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2.5 rounded-md transition-colors font-medium disabled:opacity-50"
              >
                {searchLoading ? "..." : "Search"}
              </button>
            </div>
          </form>

          {/* Search Results */}
          {searchResults && (
            <div data-testid="bible-search-results" className="mb-10 bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg font-semibold text-[#1C2E4A]">
                  {searchResults.error ? "Not Found" : searchResults.reference}
                </h3>
                <button
                  onClick={() => setSearchResults(null)}
                  className="text-sm text-[#595959] hover:text-[#1C2E4A]"
                >
                  Clear
                </button>
              </div>
              {searchResults.error ? (
                <p className="text-sm text-[#595959]">Could not find that reference. Try something like "John 3:16" or "Psalm 23".</p>
              ) : (
                <div className="space-y-2">
                  {searchResults.verses?.map((v) => (
                    <p key={`${v.book_name}-${v.chapter}-${v.verse}`} className="text-sm text-[#595959] leading-relaxed">
                      <span className="font-semibold text-[#C8A97E] text-xs mr-1">{v.verse}</span>
                      {v.text}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Navigation breadcrumb */}
          {view !== "books" && (
            <button
              data-testid="bible-back-btn"
              onClick={goBack}
              className="flex items-center gap-2 text-sm text-[#1C2E4A] hover:text-[#C8A97E] transition-colors font-medium mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              {view === "chapters" ? "All Books" : selectedBook?.name}
            </button>
          )}

          {/* Book Selection */}
          {view === "books" && (
            <div data-testid="bible-books-view">
              <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-4">Old Testament</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-10">
                {otBooks.map((book) => (
                  <button
                    key={book.name}
                    data-testid={`book-${book.name.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => { setSelectedBook(book); setView("chapters"); }}
                    className="text-left text-sm p-3 rounded-lg border border-[#E5E2DC] hover:border-[#C8A97E] hover:bg-[#C8A97E]/5 transition-all text-[#1C2E4A] font-medium flex items-center justify-between"
                  >
                    {book.name}
                    <ChevronRight className="w-3.5 h-3.5 text-[#595959]" />
                  </button>
                ))}
              </div>

              <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-4">New Testament</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {ntBooks.map((book) => (
                  <button
                    key={book.name}
                    data-testid={`book-${book.name.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => { setSelectedBook(book); setView("chapters"); }}
                    className="text-left text-sm p-3 rounded-lg border border-[#E5E2DC] hover:border-[#C8A97E] hover:bg-[#C8A97E]/5 transition-all text-[#1C2E4A] font-medium flex items-center justify-between"
                  >
                    {book.name}
                    <ChevronRight className="w-3.5 h-3.5 text-[#595959]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chapter Selection */}
          {view === "chapters" && selectedBook && (
            <div data-testid="bible-chapters-view">
              <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-6">
                {selectedBook.name}
              </h2>
              <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((ch) => (
                  <button
                    key={ch}
                    data-testid={`chapter-${ch}`}
                    onClick={() => fetchChapter(selectedBook.name, ch)}
                    className="w-full aspect-square flex items-center justify-center rounded-lg border border-[#E5E2DC] hover:border-[#C8A97E] hover:bg-[#C8A97E]/5 transition-all text-sm font-medium text-[#1C2E4A]"
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reading View */}
          {view === "reading" && (
            <div data-testid="bible-reading-view">
              <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-2">
                {selectedBook?.name} {selectedChapter}
              </h2>
              <p className="text-xs text-[#595959] mb-6 uppercase tracking-wider">King James Version</p>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 text-[#C8A97E] animate-spin" />
                </div>
              ) : (
                <div className="space-y-3">
                  {verses.map((v) => (
                    <p key={v.verse} className="text-base text-[#595959] leading-relaxed">
                      <span className="font-semibold text-[#C8A97E] text-xs mr-1.5 align-super">{v.verse}</span>
                      {v.text}
                    </p>
                  ))}
                </div>
              )}

              {/* Chapter navigation */}
              {!loading && selectedBook && (
                <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E5E2DC]">
                  <button
                    disabled={selectedChapter <= 1}
                    onClick={() => fetchChapter(selectedBook.name, selectedChapter - 1)}
                    className="text-sm text-[#1C2E4A] hover:text-[#C8A97E] font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    &larr; Previous Chapter
                  </button>
                  <span className="text-xs text-[#595959]">Chapter {selectedChapter} of {selectedBook.chapters}</span>
                  <button
                    disabled={selectedChapter >= selectedBook.chapters}
                    onClick={() => fetchChapter(selectedBook.name, selectedChapter + 1)}
                    className="text-sm text-[#1C2E4A] hover:text-[#C8A97E] font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Next Chapter &rarr;
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
